import { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, KeyRound, Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { client, unwrap } from "@/lib/api-base";
import { formatApiError } from "@/lib/api-error";

/**
 * Tab "Sicurezza" — cambio password dell'utente unico.
 *
 * Il backend riscrive `backend/.env` con il nuovo hash bcrypt.
 * Non serve restart del server: `os.environ` viene aggiornato
 * in-process, quindi la nuova password è attiva immediatamente.
 *
 * Requisiti operativi:
 *  - autocomplete="new-password" ovunque (browser non memorizza)
 *  - min 8 caratteri, deve differire dalla corrente
 *  - conferma password digitata due volte (evita typo)
 *  - dopo il cambio riuscito, i campi vengono azzerati
 */
export function SecurityTab() {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
    setError("");
  };

  const canSubmit =
    currentPwd.length > 0 &&
    newPwd.length >= 8 &&
    newPwd === confirmPwd &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Guard-rail lato client
    if (newPwd.length < 8) {
      setError("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    if (newPwd !== confirmPwd) {
      setError("Le due password non coincidono.");
      return;
    }
    if (newPwd === currentPwd) {
      setError("La nuova password deve essere diversa da quella corrente.");
      return;
    }
    setSubmitting(true);
    try {
      await unwrap(
        client.post("/auth/change-password", {
          current_password: currentPwd,
          new_password: newPwd,
        }),
      );
      toast.success("Password aggiornata correttamente.");
      reset();
    } catch (err) {
      const msg = formatApiError(err, "Errore durante il cambio password");
      setError(msg);
      // Su errore lascio i campi (l'utente potrebbe voler correggere)
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 text-sm text-emerald-900">
        <div className="mb-1 flex items-center gap-2 font-semibold">
          <ShieldCheck size={14} /> Cambio password
        </div>
        <p className="text-xs leading-relaxed text-emerald-800/80">
          Aggiorna la password di accesso al gestionale. Verrà attiva
          immediatamente; le sessioni già aperte non vengono invalidate
          fino al successivo logout / auto-logout per inattività.
        </p>
      </div>

      <form
        onSubmit={submit}
        autoComplete="off"
        className="space-y-4 rounded-xl border border-stone-200 bg-white p-5"
        data-testid="form-change-password"
      >
        {error && (
          <div
            data-testid="change-password-error"
            className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
          >
            <AlertCircle size={13} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-stone-500">
            Password attuale
          </Label>
          <div className="relative">
            <Lock
              size={13}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <Input
              data-testid="input-current-password"
              type="password"
              autoComplete="new-password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              className="pl-9"
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-stone-500">
            Nuova password
          </Label>
          <div className="relative">
            <KeyRound
              size={13}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <Input
              data-testid="input-new-password"
              type="password"
              autoComplete="new-password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              className="pl-9"
              placeholder="Almeno 8 caratteri"
              required
              minLength={8}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-stone-500">
            Conferma nuova password
          </Label>
          <div className="relative">
            <KeyRound
              size={13}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <Input
              data-testid="input-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              className="pl-9"
              required
              minLength={8}
            />
          </div>
          {confirmPwd.length > 0 && confirmPwd !== newPwd && (
            <p className="text-[11px] text-rose-600">
              Le due password non coincidono.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={reset}
            disabled={submitting}
            data-testid="btn-reset-password-form"
          >
            Annulla
          </Button>
          <Button
            type="submit"
            data-testid="btn-submit-change-password"
            disabled={!canSubmit}
          >
            {submitting ? "Aggiornamento…" : "Aggiorna password"}
          </Button>
        </div>
      </form>

      <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4 text-xs leading-relaxed text-stone-600">
        <div className="mb-1 font-semibold text-stone-700">
          Suggerimenti sicurezza
        </div>
        <ul className="ml-4 list-disc space-y-0.5">
          <li>Usa almeno 12 caratteri con lettere, numeri e simboli.</li>
          <li>Non riutilizzare password già in uso per altri servizi.</li>
          <li>
            Il gestionale non memorizza mai la password: verrà richiesta
            ad ogni accesso e dopo 10 minuti di inattività.
          </li>
        </ul>
      </div>
    </div>
  );
}
